import React, { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import "../features.css";
import { FEATURES } from "../../config/features";
import { useAuth } from "../../auth/AuthProvider";
import { tierCan } from "../../auth/permissions";
import { ownSplitFor, resolveAgentName, useBookData } from "../../data/BookDataProvider";
import { formatDate, formatMoney, renewalInfo } from "../../data/format";
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Skeleton,
  SkeletonText,
  Tabs,
  BookIcon,
  CarIcon,
  ChevronLeftIcon,
  HomeIcon,
  ServiceIcon,
  UmbrellaIcon
} from "../../ui";
import {
  LobBadge,
  OpenCasesCard,
  PolicyStatusBadge,
  QuoteStageBadge,
  RenewalBadge,
  openCasesForClient
} from "../shared";
import PolicyDrawer from "./PolicyDrawer";
import ProposalDownloadSlot from "./ProposalDownloadSlot";
import ClientEditDrawer from "./ClientEditDrawer";
import DocumentsTab from "./DocumentsTab";
import ActivityTab from "./ActivityTab";
import ReferralOverview from "./ReferralOverview";

/**
 * The client household hub (§3.8) — the new center of the product. Every
 * path (Book row, Today feed, ⌘K) lands here; every fact about a household
 * lives under one bookmarkable URL, with the active tab in the query string
 * so links like /app/clients/:id?tab=service work.
 *
 * Data comes from the composed book store — policies/quotes/requests are
 * matched BY QUERY (clientRef/clientID), never via the client's inverse
 * arrays, which are empty on the whole imported book.
 */

const LOB_ICONS = { auto: CarIcon, home: HomeIcon, umbrella: UmbrellaIcon };

function PolicyCard({ policy, onClick }) {
  const Icon = LOB_ICONS[policy.lobKey] || HomeIcon;
  const renewal = renewalInfo(policy);
  return (
    <button type="button" className="feat-policy-card" onClick={onClick}>
      <span className="feat-policy-card__icon">
        <Icon size={18} />
      </span>
      <span className="feat-policy-card__body">
        <span className="feat-cell-primary">
          {policy.lob} · {policy.carrier || "Unknown carrier"}
        </span>
        <span className="feat-cell-secondary">
          {policy.policyNumber ? `#${policy.policyNumber}` : "No policy number"}
        </span>
      </span>
      <span className="feat-policy-card__right">
        <span className="feat-cell-primary">{formatMoney(policy.premium)}/yr</span>
        <RenewalBadge renewal={renewal} />
      </span>
    </button>
  );
}

function OverviewTab({ household, can, agents, openCases, onOpenPolicy }) {
  const { client } = household;
  const agentName = resolveAgentName(agents, client.agentRef || client.agent1);
  return (
    <div className="feat-hub-grid">
      <div>
        {openCases && openCases.length > 0 ? (
          <div style={{ marginBottom: "var(--space-4)" }}>
            <OpenCasesCard cases={openCases} />
          </div>
        ) : null}
        {household.activePolicies.length === 0 ? (
          <Card title="Policies">
            <EmptyState
              icon={<BookIcon size={32} />}
              title="No active policies"
              message="This household has no in-force policies on file."
            />
          </Card>
        ) : (
          <div className="feat-policy-cards">
            {household.activePolicies.map((p) => (
              <PolicyCard key={p._id} policy={p} onClick={() => onOpenPolicy(p)} />
            ))}
          </div>
        )}

        {can.seeInternalNotes && client.notes ? (
          <div className="feat-kv-section">
            <Card title="Notes">
              <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{client.notes}</p>
            </Card>
          </div>
        ) : null}
      </div>

      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        <Card title="Contact">
          <div className="feat-kv">
            <div className="feat-kv__row">
              <span className="feat-kv__key">Email</span>
              <span className="feat-kv__value">{client.email || "—"}</span>
            </div>
            <div className="feat-kv__row">
              <span className="feat-kv__key">Phone</span>
              <span className="feat-kv__value">{client.telephone || "—"}</span>
            </div>
            <div className="feat-kv__row">
              <span className="feat-kv__key">Address</span>
              <span className="feat-kv__value">{client.address || "—"}</span>
            </div>
          </div>
        </Card>

        {household.memberCount > 0 ? (
          <Card title="Household">
            <div className="feat-kv">
              {client.household.map((member, i) => (
                <div key={i} className="feat-kv__row">
                  <span className="feat-kv__key">{member.name}</span>
                  <span className="feat-kv__value">{member.detail || ""}</span>
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        <Card title="Servicing">
          <div className="feat-kv">
            <div className="feat-kv__row">
              <span className="feat-kv__key">Agent</span>
              <span className="feat-kv__value">{agentName || "Unassigned"}</span>
            </div>
            <div className="feat-kv__row">
              <span className="feat-kv__key">Client since</span>
              <span className="feat-kv__value">{formatDate(client.created_at)}</span>
            </div>
            {can.isStaff && client.retentionStatus ? (
              <div className="feat-kv__row">
                <span className="feat-kv__key">Retention</span>
                <span className="feat-kv__value">{client.retentionStatus}</span>
              </div>
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  );
}

function PoliciesTab({ household, can, identity, onOpenPolicy }) {
  const columns = [
    {
      key: "lob",
      header: "Line",
      sortValue: (p) => p.lob,
      render: (p) => <LobBadge lobKey={p.lobKey} lob={p.lob} />
    },
    { key: "carrier", header: "Carrier", render: (p) => p.carrier || "—" },
    { key: "policyNumber", header: "Policy #", render: (p) => p.policyNumber || "—" },
    {
      key: "status",
      header: "Status",
      sortValue: (p) => (p.policyActive === false ? 1 : 0),
      render: (p) => <PolicyStatusBadge policy={p} />
    },
    {
      key: "effective",
      header: "Effective",
      sortValue: (p) => (p.effectiveDate ? new Date(p.effectiveDate).getTime() : null),
      render: (p) => formatDate(p.effectiveDate)
    },
    {
      key: "renewal",
      header: "Renews",
      sortValue: (p) => {
        const r = renewalInfo(p);
        return r ? r.date.getTime() : null;
      },
      render: (p) => <RenewalBadge renewal={renewalInfo(p)} />
    },
    {
      key: "premium",
      header: "Premium",
      align: "num",
      sortValue: (p) => Number(p.premium) || 0,
      render: (p) => formatMoney(p.premium)
    }
  ];
  if (!can.seeOtherSplits) {
    columns.push({
      key: "split",
      header: "Your split",
      align: "num",
      sortValue: (p) => ownSplitFor(p, identity),
      render: (p) => {
        const split = ownSplitFor(p, identity);
        return split == null ? (
          <span className="fk-muted">—</span>
        ) : (
          <Badge variant="brand">{split}%</Badge>
        );
      }
    });
  }
  return (
    <DataTable
      columns={columns}
      rows={household.policies}
      rowKey={(p) => p._id}
      onRowClick={onOpenPolicy}
      rowClassName={(p) => (p.policyActive === false ? "feat-row-inactive" : "")}
      countLabel="policies"
      emptyState={
        <EmptyState
          icon={<BookIcon size={32} />}
          title="No policies"
          message="No policies on file for this household, active or inactive."
        />
      }
    />
  );
}

function QuotesTab({ household }) {
  const columns = [
    {
      key: "name",
      header: "Quote",
      render: (q) =>
        q.quickName || [q.firstName, q.lastName].filter(Boolean).join(" ") || "Unnamed quote"
    },
    {
      key: "stage",
      header: "Stage",
      sortable: false,
      render: (q) => <QuoteStageBadge quote={q} />
    },
    {
      key: "target",
      header: "Target date",
      sortValue: (q) => (q.targetDate ? new Date(q.targetDate).getTime() : null),
      render: (q) => formatDate(q.targetDate)
    },
    {
      key: "premium",
      header: "Target premium",
      align: "num",
      sortValue: (q) => Number(q.targetPremium) || 0,
      render: (q) => (q.targetPremium ? formatMoney(q.targetPremium) : "—")
    }
  ];
  // D6: staff upload/download the quote's proposal PDF (this tab is staff-only).
  if (FEATURES.proposalDocs) {
    columns.push({
      key: "proposal",
      header: "Proposal",
      sortable: false,
      render: (q) => <ProposalDownloadSlot quoteId={q._id} canUpload />
    });
  }
  return (
    <DataTable
      columns={columns}
      rows={household.quotes}
      rowKey={(q) => q._id}
      countLabel="quotes"
      emptyState={
        <EmptyState
          title="No quotes linked"
          message="Quotes link to a household on conversion. Pre-conversion quotes live in the Pipeline (arriving in the next phase)."
        />
      }
    />
  );
}

function ServiceTab({ household }) {
  if (household.requests.length === 0) {
    return (
      <EmptyState
        icon={<ServiceIcon size={32} />}
        title="No service requests"
        message="Requests for this household will appear here. Creating one from this page arrives with the Service phase."
      />
    );
  }
  return (
    <div className="feat-feed" role="list">
      {household.requests.map((r) => (
        <div key={r._id} className="feat-feed__row" style={{ cursor: "default" }} role="listitem">
          <Badge variant={r.completed ? "success" : "warning"} dot>
            {r.completed ? "Resolved" : "Open"}
          </Badge>
          <span className="feat-feed__main">
            <span className="feat-feed__title" style={{ whiteSpace: "normal" }}>
              {r.type ? `${r.type}: ` : ""}
              {r.message}
            </span>
            <span className="feat-feed__meta">{formatDate(r.created_at)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function ClientHubPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const { tier, agents, identity } = useAuth();
  const can = tierCan(tier);
  // E QA L-5: one name per tier — the referral rail says "My book", so the
  // hub's back-links must too (not the staff "Book of Business").
  const bookLabel = can.seeAgentAttribution ? "Book of Business" : "My book";
  const { loading, getHousehold, prospects, refresh } = useBookData();
  const [openPolicy, setOpenPolicy] = useState(null);
  const [editingClient, setEditingClient] = useState(false);

  const household = getHousehold(id);

  // In-flight retention cases for this household (Phase 3, §10.6) — absent,
  // not empty, when there are none or the flag is off.
  const openCases = useMemo(
    () => (household && FEATURES.retention ? openCasesForClient(prospects, household.id) : []),
    [household, prospects]
  );

  const tabs = useMemo(() => {
    if (!household) {
      return [];
    }
    // Referral tier (E §5): two tabs only — Overview reads as complete,
    // with the D2 policy rows inline; no Documents/Quotes/Service tabs
    // implying withheld content.
    if (!can.isStaff) {
      return [
        { key: "overview", label: "Overview" },
        { key: "activity", label: "Activity" }
      ];
    }
    const list = [
      { key: "overview", label: "Overview" },
      { key: "policies", label: "Policies", count: household.policies.length },
      { key: "quotes", label: "Quotes", count: household.quotes.length },
      { key: "service", label: "Service", count: household.requests.length }
    ];
    if (can.seeDocuments) {
      list.push({ key: "documents", label: "Documents" });
    }
    list.push({ key: "activity", label: "Activity" });
    return list;
  }, [household, can.seeDocuments, can.isStaff]);

  if (loading) {
    return (
      <div className="shell-page">
        <div className="feat-hub-header">
          <Skeleton height={14} width={120} />
          <Skeleton height={24} width={280} />
          <Skeleton height={14} width={420} />
        </div>
        <SkeletonText lines={6} />
      </div>
    );
  }

  if (!household) {
    return (
      <div className="shell-page">
        <Card>
          <EmptyState
            icon={<BookIcon size={32} />}
            title="Client not found"
            message="This client isn't in your book — or the link is out of date. If you believe you should have access, contact an administrator."
            action={
              <Link to="/app/book">
                <Button variant="primary">Back to {bookLabel}</Button>
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  const { client } = household;
  const activeTab = tabs.some((t) => t.key === tab) ? tab : "overview";

  return (
    <div className="shell-page">
      <div className="feat-hub-header">
        <Link to="/app/book" className="feat-backlink">
          <ChevronLeftIcon size={14} />
          {bookLabel}
        </Link>
        <div className="feat-hub-header__toprow">
          <h1 className="feat-hub-header__name">{household.name}</h1>
          {household.activePolicies.length > 0 ? (
            <Badge variant="success" dot>
              Active client
            </Badge>
          ) : (
            <Badge variant="neutral">No active policies</Badge>
          )}
          <span style={{ flex: 1 }} />
          {can.editRecords ? (
            <Button variant="secondary" onClick={() => setEditingClient(true)}>
              Edit
            </Button>
          ) : null}
        </div>
        <div className="feat-hub-header__meta">
          {client.address ? <span>{client.address}</span> : null}
          {client.email ? <span>{client.email}</span> : null}
          {client.telephone ? <span>{client.telephone}</span> : null}
        </div>
      </div>

      <Tabs
        tabs={tabs}
        active={activeTab}
        onChange={(next) =>
          setSearchParams(next === "overview" ? {} : { tab: next }, { replace: true })
        }
      />

      <div className="feat-hub-tabpanel">
        {activeTab === "overview" && !can.isStaff ? (
          <ReferralOverview household={household} identity={identity} openCases={openCases} />
        ) : null}
        {activeTab === "overview" && can.isStaff ? (
          <OverviewTab
            household={household}
            can={can}
            agents={agents}
            openCases={openCases}
            onOpenPolicy={setOpenPolicy}
          />
        ) : null}
        {activeTab === "policies" ? (
          <PoliciesTab
            household={household}
            can={can}
            identity={identity}
            onOpenPolicy={setOpenPolicy}
          />
        ) : null}
        {activeTab === "quotes" ? <QuotesTab household={household} /> : null}
        {activeTab === "service" ? <ServiceTab household={household} /> : null}
        {activeTab === "documents" && can.seeDocuments ? (
          <DocumentsTab household={household} />
        ) : null}
        {activeTab === "activity" ? <ActivityTab household={household} /> : null}
      </div>

      {can.isStaff ? (
        <PolicyDrawer
          policy={openPolicy}
          onClose={() => setOpenPolicy(null)}
          onSaved={() => {
            setOpenPolicy(null);
            refresh();
          }}
        />
      ) : null}

      <ClientEditDrawer
        household={household}
        open={editingClient}
        onClose={() => setEditingClient(false)}
        onSaved={() => {
          setEditingClient(false);
          refresh();
        }}
      />
    </div>
  );
}

export default ClientHubPage;
